export const ENTITY_CONFIG = {
  suspects: {
    discoveredKey: 'discoveredSuspects',
    nestedDiscoveredKey: 'suspects',
    backendDiscoveredKey: 'discovered_suspects',
    backendObjectsKey: 'suspects',
  },
  clues: {
    discoveredKey: 'discoveredClues',
    nestedDiscoveredKey: 'clues',
    backendDiscoveredKey: 'discovered_clues',
    backendObjectsKey: 'clues',
  },
  locations: {
    discoveredKey: 'discoveredLocations',
    nestedDiscoveredKey: 'locations',
    backendDiscoveredKey: 'discovered_locations',
    backendObjectsKey: 'locations',
  },
  documents: {
    discoveredKey: 'discoveredDocuments',
    nestedDiscoveredKey: 'documents',
    backendDiscoveredKey: 'discovered_documents',
    backendObjectsKey: 'documents',
  },
};

const toArray = (value) => (Array.isArray(value) ? value : []);

const uniqueAppend = (existing = [], incoming = []) => {
  const result = [];
  const seen = new Set();

  [...toArray(existing), ...toArray(incoming)].forEach((id) => {
    if (!id || seen.has(id)) {
      return;
    }
    seen.add(id);
    result.push(id);
  });

  return result;
};

const mergeObjectsById = (existing = [], incoming = []) => {
  const byId = new Map();
  const order = [];

  [...toArray(existing), ...toArray(incoming)].forEach((item) => {
    if (!item?.id) {
      return;
    }
    if (!byId.has(item.id)) {
      order.push(item.id);
    }
    byId.set(item.id, item);
  });

  return order.map((id) => byId.get(id));
};

const readDiscoveredIds = (updatedState, config) => {
  if (Array.isArray(updatedState?.[config.backendDiscoveredKey])) {
    return updatedState[config.backendDiscoveredKey];
  }

  if (Array.isArray(updatedState?.discovered?.[config.nestedDiscoveredKey])) {
    return updatedState.discovered[config.nestedDiscoveredKey];
  }

  return [];
};

const getKnownIds = (objects) => new Set(toArray(objects).map((item) => item?.id).filter(Boolean));

export const flattenGameStateEntities = (state = {}) => ({
  suspects: state.suspects || [],
  clues: state.clues || [],
  locations: state.locations || [],
  documents: state.documents || [],
  discoveredSuspects: state.discoveredSuspects ?? state.discovered?.suspects ?? [],
  discoveredClues: state.discoveredClues ?? state.discovered?.clues ?? [],
  discoveredLocations: state.discoveredLocations ?? state.discovered?.locations ?? [],
  discoveredDocuments: state.discoveredDocuments ?? state.discovered?.documents ?? [],
  newlyDiscovered: state.newlyDiscovered || {
    suspects: [],
    clues: [],
    locations: [],
    documents: [],
  },
});

export const nestActionEntityState = (flatState = {}) => ({
  suspects: flatState.suspects || [],
  clues: flatState.clues || [],
  locations: flatState.locations || [],
  documents: flatState.documents || [],
  discovered: {
    suspects: flatState.discoveredSuspects || [],
    clues: flatState.discoveredClues || [],
    locations: flatState.discoveredLocations || [],
    documents: flatState.discoveredDocuments || [],
  },
});

export const appendRevealDiff = (newlyDiscovered = {}, revealDiff = {}) => ({
  suspects: uniqueAppend(newlyDiscovered.suspects, revealDiff.suspects),
  clues: uniqueAppend(newlyDiscovered.clues, revealDiff.clues),
  locations: uniqueAppend(newlyDiscovered.locations, revealDiff.locations),
  documents: uniqueAppend(newlyDiscovered.documents, revealDiff.documents),
});

export const buildSessionRefreshCompletionUpdate = ({
  didRefresh,
  currentNewlyDiscovered = {},
  revealDiff = null,
} = {}) => {
  const update = {
    needsSessionRefresh: !didRefresh,
    actionProcessing: false,
  };

  if (didRefresh && revealDiff) {
    update.newlyDiscovered = appendRevealDiff(currentNewlyDiscovered, revealDiff);
  }

  return update;
};

export const applyActionResult = (previousState = {}, payload = {}) => {
  const updatedState = payload?.updatedState || payload?.updated_state || {};
  const nextState = { ...flattenGameStateEntities(previousState) };
  const revealDiff = {
    suspects: [],
    clues: [],
    locations: [],
    documents: [],
  };
  const warnings = [];

  Object.entries(ENTITY_CONFIG).forEach(([entityType, config]) => {
    const previousObjects = nextState[entityType];
    const incomingObjects = updatedState?.[config.backendObjectsKey];
    const mergedObjects = Array.isArray(incomingObjects)
      ? mergeObjectsById(previousObjects, incomingObjects)
      : previousObjects;

    const previousDiscovered = nextState[config.discoveredKey];
    const incomingDiscovered = readDiscoveredIds(updatedState, config);
    const mergedDiscovered = uniqueAppend(previousDiscovered, incomingDiscovered);
    const previousDiscoveredSet = new Set(previousDiscovered);
    const knownIds = getKnownIds(mergedObjects);

    nextState[entityType] = mergedObjects;
    nextState[config.discoveredKey] = mergedDiscovered;
    revealDiff[entityType] = mergedDiscovered.filter((id) => !previousDiscoveredSet.has(id));

    revealDiff[entityType].forEach((id) => {
      if (!knownIds.has(id)) {
        warnings.push({
          code: 'revealed_object_missing',
          entityType,
          id,
        });
      }
    });
  });

  return {
    nextState,
    revealDiff,
    needsSessionRefresh: warnings.length > 0,
    warnings,
  };
};
